import * as _ from "lodash-es";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import ISupplierModel from "../../../../models/ISupplierModel";
import OrderStatesEnum from "../../../../models/OrderStatesEnum";

/** A state the business can add to a workflow, with the group it is listed under in the picker. */
export interface IWorkflowStateOption {
    order_state_id: OrderStatesEnum;
    name: string;
    groupKey: "workflow_group_entry" | "workflow_group_review" | "workflow_group_accounting" | "workflow_group_production";
}

const entry = (order_state_id: OrderStatesEnum, name: string, groupKey: IWorkflowStateOption["groupKey"]): IWorkflowStateOption => ({
    order_state_id,
    name,
    groupKey,
});

/** Every state a workflow may contain besides Preliminary and the fulfillment states. */
export const WORKFLOW_STATE_OPTIONS: IWorkflowStateOption[] = [
    entry(OrderStatesEnum.SUBMITTED, "submitted", "workflow_group_entry"),
    entry(OrderStatesEnum.IN_REVIEW, "in_review", "workflow_group_review"),
    entry(OrderStatesEnum.ACCEPTED, "accepted", "workflow_group_review"),
    entry(OrderStatesEnum.SCHEDULED, "scheduled", "workflow_group_review"),
    entry(OrderStatesEnum.CONFIRMED, "confirmed", "workflow_group_review"),
    entry(OrderStatesEnum.COMPLETED, "completed", "workflow_group_accounting"),
    entry(OrderStatesEnum.VENDOR_APPROVAL_PAYMENT, "vendor_approval_payment", "workflow_group_accounting"),
    entry(OrderStatesEnum.ACCOUNTING, "accounting", "workflow_group_accounting"),
    entry(OrderStatesEnum.PAYMENT_RECEIVED_ORDER_BEING_PACKED, "payment_received_order_being_packed", "workflow_group_accounting"),
    entry(
        OrderStatesEnum.PACKING_COMPLETED_AWAITING_SHIPPING_INFO,
        "packing_completed_awaiting_shipping_info",
        "workflow_group_accounting"
    ),
    entry(OrderStatesEnum.DEPOSIT_RECEIVED_ORDER_PACKING, "deposit_received_order_packing", "workflow_group_accounting"),
    entry(
        OrderStatesEnum.PACKING_COMPLETED_AWAITING_PAYMENT_BALANCE_TO_SHIP,
        "packing_completed_awaiting_payment_balance_to_ship",
        "workflow_group_accounting"
    ),
    entry(OrderStatesEnum.PROCESSED, "processed", "workflow_group_production"),
    entry(OrderStatesEnum.PRINTED, "printed", "workflow_group_production"),
    entry(OrderStatesEnum.MOUNTED, "mounted", "workflow_group_production"),
    entry(OrderStatesEnum.LAMINATED, "laminated", "workflow_group_production"),
    entry(OrderStatesEnum.IN_QUEUE, "in_queue", "workflow_group_production"),
    entry(OrderStatesEnum.AWAITING_REVIEW, "awaiting_review", "workflow_group_production"),
    entry(OrderStatesEnum.UNDER_REVIEW, "under_review", "workflow_group_production"),
    entry(OrderStatesEnum.APPROVED, "approved", "workflow_group_production"),
    entry(OrderStatesEnum.IN_PROGRESS_PACKING, "in_progress_packing", "workflow_group_production"),
    entry(OrderStatesEnum.FINISHING, "finishing", "workflow_group_production"),
    entry(OrderStatesEnum.SUBMITTED_TO_PRODUCTION, "submitted_to_production", "workflow_group_production"),
    entry(OrderStatesEnum.WELD_COMPLETED, "weld_completed", "workflow_group_production"),
    entry(OrderStatesEnum.INITIAL_QC_COMPLETED, "initial_qc_completed", "workflow_group_production"),
    entry(OrderStatesEnum.PAINT_COMPLETED, "paint_completed", "workflow_group_production"),
    entry(OrderStatesEnum.FINAL_QC_COMPLETED, "final_qc_completed", "workflow_group_production"),
    entry(OrderStatesEnum.PENDING, "pending", "workflow_group_production"),
    entry(OrderStatesEnum.ACTIVE, "active", "workflow_group_production"),
    entry(OrderStatesEnum.IN_DESIGN, "in_design", "workflow_group_production"),
    entry(OrderStatesEnum.SENT_TO_PHARMACY, "sent_to_pharmacy", "workflow_group_production"),
    entry(OrderStatesEnum.READY_FOR_PICK, "ready_for_pick", "workflow_group_production"),
    entry(OrderStatesEnum.PRE_PRESS, "pre_press", "workflow_group_production"),
    entry(OrderStatesEnum.READY_TO_PRINT, "ready_to_print", "workflow_group_production"),
    entry(OrderStatesEnum.PRINTING, "printing", "workflow_group_production"),
    entry(OrderStatesEnum.MATERIAL_CUTTING, "material_cutting", "workflow_group_production"),
    entry(OrderStatesEnum.SEWING, "sewing", "workflow_group_production"),
    entry(OrderStatesEnum.NEW, "new", "workflow_group_production"),
    entry(OrderStatesEnum.WAREHOUSE, "warehouse", "workflow_group_production"),
    entry(OrderStatesEnum.BILLED, "billed", "workflow_group_production"),
    entry(OrderStatesEnum.PARTS_ORDERED, "parts_ordered", "workflow_group_production"),
    entry(OrderStatesEnum.RECEIVED, "received", "workflow_group_production"),
    entry(OrderStatesEnum.TRANSFERRED, "transferred", "workflow_group_production"),
];

/** States the Shipping settings add and order; the editor shows them locked. */
export const FULFILLMENT_STATE_IDS = new Set<OrderStatesEnum>([
    OrderStatesEnum.READY_TO_SHIP,
    OrderStatesEnum.PARTIALLY_SHIPPED,
    OrderStatesEnum.SHIPPED,
    OrderStatesEnum.READY_FOR_DELIVERY,
    OrderStatesEnum.DELIVERED,
    OrderStatesEnum.READY_FOR_PICKUP,
    OrderStatesEnum.PICKED_UP,
]);

export const isFulfillmentStep = (step: IOrder_Progress_State): boolean => FULFILLMENT_STATE_IDS.has(step.order_state_id);

export const isPreliminaryStep = (step: IOrder_Progress_State): boolean => step.order_state_id === OrderStatesEnum.PRELIMINARY;

export interface IWorkflowParts {
    preliminary: IOrder_Progress_State | null;
    /** Steps the user may reorder, remove and add to. */
    editable: IOrder_Progress_State[];
    fulfillment: IOrder_Progress_State[];
    final: IOrder_Progress_State | null;
}

/** Splits a saved step list into the parts the editor shows, keeping each part's relative order. */
export const splitWorkflowSteps = (steps: IOrder_Progress_State[]): IWorkflowParts => {
    const ordered = _.sortBy(steps || [], (step) => step.step_no);

    return {
        preliminary: ordered.find(isPreliminaryStep) || null,
        editable: ordered.filter((step) => !isPreliminaryStep(step) && !isFulfillmentStep(step) && !step.isFinalStep),
        fulfillment: ordered.filter((step) => isFulfillmentStep(step) && !step.isFinalStep),
        final: ordered.find((step) => step.isFinalStep && !isPreliminaryStep(step)) || null,
    };
};

/** Rebuilds the saved shape: Preliminary, the editable steps, the fulfillment steps, then the final step, renumbered. */
export const joinWorkflowSteps = (parts: IWorkflowParts): IOrder_Progress_State[] => {
    const all = [
        ...(parts.preliminary ? [parts.preliminary] : []),
        ...parts.editable,
        ...parts.fulfillment,
        ...(parts.final ? [parts.final] : []),
    ];

    return all.map((step, index) => ({ ...step, step_no: index + 1, isFinalStep: parts.final ? step === parts.final : false }));
};

/** The order the tracker and the guide walk, as the editor shows it. */
export const orderedWorkflowSteps = (steps: IOrder_Progress_State[]): IOrder_Progress_State[] =>
    joinWorkflowSteps(splitWorkflowSteps(steps));

/** The step with `name` as its label and nothing else set, ready to be added. */
export const newWorkflowStep = (option: IWorkflowStateOption): IOrder_Progress_State => ({
    order_state_id: option.order_state_id,
    name: option.name,
    step_no: 0,
});

/** Read-only flags that, once set on a step, hold for every later step. */
export type WorkflowCarryKey =
    | "isVendorReadOnly"
    | "isSupplierReadOnly"
    | "isSupplierAdminReadOnly"
    | "allowRemake"
    | "calculateShippingAt"
    | "collectPayment"
    | "onlyCollectCcToUse";

/** The earlier step that already set `key`, if any, so the editor can say "Set at <step>". */
export const stepThatSets = (
    steps: IOrder_Progress_State[],
    step: IOrder_Progress_State,
    key: WorkflowCarryKey
): IOrder_Progress_State | null => {
    const ordered = orderedWorkflowSteps(steps);
    const index = ordered.findIndex((candidate) => candidate.order_state_id === step.order_state_id);

    for (let i = 0; i < index; i++) {
        if (ordered[i][key]) {
            return ordered[i];
        }
    }

    return null;
};

/** True when `key` holds at `step`: set on it or on an earlier step. */
export const holdsAt = (steps: IOrder_Progress_State[], step: IOrder_Progress_State, key: WorkflowCarryKey): boolean => {
    return !!step[key] || !!stepThatSets(steps, step, key);
};

export type WorkflowChipTone = "neutral" | "email" | "readonly" | "brand";

export interface IWorkflowChipSpec {
    id: string;
    tone: WorkflowChipTone;
    /** Font Awesome class, without the style prefix. */
    icon?: string;
    labelKey: string;
    /** Translation keys of the names to list in the label's {recipients} slot. */
    names?: string[];
    /** The label's "vendor" is a purchasing vendor source, not the renamed customer-facing vendor. */
    keepVendorWord?: boolean;
}

/** Recipient flags in display order, with the chip label of each. */
export interface IStepFlagOption {
    key: keyof IOrder_Progress_State;
    /** Short name for chips and the guide ("Order user"). */
    labelKey: string;
    /** Label of the option row in the step panel ("Email User"). */
    optionLabelKey: string;
    when?: (supplier: ISupplierModel) => boolean;
}

/** Recipient flags in display order. */
export const RECIPIENT_KEYS: IStepFlagOption[] = [
    { key: "email_user", labelKey: "recipient_user", optionLabelKey: "email_user" },
    { key: "email_supplier", labelKey: "recipient_internal", optionLabelKey: "email_internally" },
    {
        key: "email_vendor",
        labelKey: "recipient_vendor",
        optionLabelKey: "email_vendor",
        when: (supplier) => !!supplier.send_vendor_emails,
    },
    {
        key: "email_rep_group",
        labelKey: "recipient_rep_group",
        optionLabelKey: "email_rep_group",
        when: (supplier) => !!supplier.has_rep_groups,
    },
    { key: "email_client", labelKey: "recipient_client", optionLabelKey: "email_client", when: (supplier) => !!supplier.has_client_module },
    {
        key: "email_catalog_email",
        labelKey: "recipient_catalog_email",
        optionLabelKey: "email_catalog_email",
        when: (supplier) => !!supplier.has_catalog_email,
    },
];

export const ATTACHMENT_KEYS: IStepFlagOption[] = [
    { key: "attachOrderPdf", labelKey: "attachment_order_pdf", optionLabelKey: "attach_order_pdf" },
    { key: "attachOrderCsv", labelKey: "attachment_order_csv", optionLabelKey: "attach_order_csv" },
    { key: "attachPackingListPdf", labelKey: "attachment_packing_list_pdf", optionLabelKey: "attach_packing_list_pdf" },
];

/** The chips that summarize a step in the list: what it emails, locks and triggers. */
export const getStepChipSpecs = (step: IOrder_Progress_State, supplier: ISupplierModel): IWorkflowChipSpec[] => {
    const chips: IWorkflowChipSpec[] = [];
    const integration: any = supplier.Supplier_Integration || {};

    if (isPreliminaryStep(step)) {
        return [
            { id: "first", tone: "neutral", labelKey: "always_first" },
            { id: "everyone", tone: "neutral", labelKey: "editable_by_everyone" },
        ];
    }

    if (step.sendEmail) {
        const names = RECIPIENT_KEYS.filter((recipient) => step[recipient.key] && (!recipient.when || recipient.when(supplier))).map(
            (recipient) => recipient.labelKey
        );
        const extra = (step.email || "").split(",").filter((email) => email.trim()).length;

        chips.push({
            id: "email",
            tone: "email",
            icon: "fa-envelope",
            labelKey: names.length || extra ? "chip_emails" : "chip_emails_nobody",
            names,
        });
        ATTACHMENT_KEYS.filter((attachment) => step[attachment.key]).forEach((attachment) =>
            chips.push({ id: attachment.key as string, tone: "neutral", icon: "fa-paperclip", labelKey: attachment.labelKey })
        );
        if (step.allowCustomMessage) {
            chips.push({ id: "custom", tone: "neutral", labelKey: "chip_custom_message" });
        }
        if (step.splitEmailsByVendorSource && supplier.has_purchase_orders) {
            chips.push({ id: "split", tone: "neutral", labelKey: "chip_split_by_vendor", keepVendorWord: true });
        }
    } else {
        chips.push({ id: "noemail", tone: "neutral", labelKey: "chip_no_email" });
    }

    if (step.emailPDFPackingList) {
        chips.push({ id: "packing", tone: "email", icon: "fa-envelope", labelKey: "chip_packing_list_email" });
    }
    if (step.isAdminOnlyStep) {
        chips.push({ id: "admin", tone: "neutral", icon: "fa-user-shield", labelKey: "chip_admin_only" });
    }
    if (step.isVendorStep && supplier.has_customer_module) {
        chips.push({ id: "vendorstep", tone: "neutral", labelKey: "chip_vendor_step" });
    }
    if (step.isVendorReadOnly && supplier.has_customer_module) {
        chips.push({ id: "vro", tone: "readonly", icon: "fa-eye-slash", labelKey: "chip_vendor_read_only" });
    }
    if (step.isSupplierReadOnly) {
        chips.push({ id: "uro", tone: "readonly", icon: "fa-eye-slash", labelKey: "chip_internal_read_only" });
    }
    if (step.isSupplierAdminReadOnly) {
        chips.push({ id: "aro", tone: "readonly", icon: "fa-eye-slash", labelKey: "chip_admin_read_only" });
    }
    if (step.collectPayment && supplier.has_payments) {
        chips.push({
            id: "pay",
            tone: "neutral",
            icon: "fa-credit-card",
            labelKey: step.onlyCollectCcToUse ? "chip_stores_card" : "chip_collects_payment",
        });
    }
    if (step.submitOrderToTaxJar && integration.has_taxjar) {
        chips.push({ id: "taxjar", tone: "neutral", icon: "fa-plug", labelKey: "chip_taxjar" });
    }
    if (step.autoCreateQBOInvoice && integration.has_qbo) {
        chips.push({ id: "qbo", tone: "neutral", icon: "fa-plug", labelKey: "chip_qbo_invoice" });
    }
    if (step.autoCreateQBOEstimate && integration.has_qbo && integration.has_vistatrac) {
        chips.push({ id: "qboest", tone: "neutral", icon: "fa-plug", labelKey: "chip_qbo_estimate" });
    }
    if ((step.autoQueueSalesOrderForQBD || step.autoQueueInvoiceForQBD) && integration.has_qbd) {
        chips.push({ id: "qbd", tone: "neutral", icon: "fa-plug", labelKey: "chip_qbd" });
    }
    if (step.sendToUnanet && integration.has_unanet) {
        chips.push({ id: "unanet", tone: "neutral", icon: "fa-plug", labelKey: "chip_unanet" });
    }
    if (step.createOrderInWooCommerce && integration.has_woocommerce) {
        chips.push({ id: "woo", tone: "neutral", icon: "fa-plug", labelKey: "chip_woocommerce" });
    }
    if (step.releaseCommission && supplier.has_commissions) {
        chips.push({ id: "commission", tone: "neutral", icon: "fa-plug", labelKey: "chip_releases_commission" });
    }
    if (step.autoCreatePOsFromOrder && supplier.has_purchase_orders) {
        chips.push({ id: "pos", tone: "neutral", icon: "fa-plug", labelKey: "chip_creates_pos" });
    }
    if (step.allowCreateFulfillment && supplier.has_shipments) {
        chips.push({ id: "fulfil", tone: "neutral", icon: "fa-box", labelKey: "chip_can_create_fulfillment" });
    }
    if (step.calculateShippingAt && (supplier.has_service_levels || supplier.has_lead_times)) {
        chips.push({ id: "shipcalc", tone: "neutral", icon: "fa-truck", labelKey: "chip_calculates_shipping" });
    }
    if (step.requireSerialsAssigned && supplier.has_serials) {
        chips.push({ id: "serials", tone: "neutral", icon: "fa-barcode", labelKey: "chip_serials_required" });
    }
    if (step.allowRemake && supplier.has_remakes) {
        chips.push({ id: "remake", tone: "neutral", labelKey: "chip_remakes" });
    }

    return chips;
};

/**
 * The translation key for a business that renamed "vendor": the same substitution OwLabel makes,
 * so "recipient_vendor" becomes "recipient_customer" and needs that key in every locale.
 */
export const vendorKey = (key: string, supplier: ISupplierModel): string => {
    const override = supplier.vendor_name_override;

    return override && override !== "null" ? key.replace(/vendor/g, override) : key;
};

/** Comma list to array and back, for the email fields that store "a@x.com,b@x.com". */
export const splitEmails = (value?: string): string[] =>
    (value || "")
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);

export const joinEmails = (emails: string[]): string => emails.join(",");

/** How many steps send an email, for the collapsed row's summary. */
export const countEmailSteps = (steps: IOrder_Progress_State[]): number => (steps || []).filter((step) => step.sendEmail).length;
